import { useCallback } from 'react';
import {
  useCurrentActiveSurgicalEvent,
  WithPatientId
} from '@/modules/surgicalEvent';
import type { UseMutation, UseMutationStateOptions } from '@/store';
import { useCurrentPatientIdParam } from '@/hooks';
import { ID } from '@ianesthesie-kit/services';

export type UseMutationForCurrentPatientParam<T, ARG> = {
  useMutation: UseMutation<T, WithPatientId<ARG>>;
  mutationConfig?: UseMutationStateOptions<T, WithPatientId<ARG>>;
};

export const useMutationForCurrentPatient = <
  T,
  ARG extends object | void = Partial<T>
>({
  useMutation,
  mutationConfig
}: UseMutationForCurrentPatientParam<T, ARG>) => {
  const patientId = useCurrentPatientIdParam()!;
  const [mutate, ...mutationProps] = useMutation(mutationConfig);

  const handleMutation = useCallback(
    (arg: Omit<ARG, 'patientId'>) =>
      mutate({ ...arg, patientId } as any).unwrap(),
    [mutate, patientId]
  );

  return [handleMutation, ...mutationProps] as const;
};

type WithSurgicalEventId<P> = P & {
  surgicalEventId: ID;
};

export type UseMutationForCurrentSurgicalEventParam<T, ARG> = {
  useMutation: UseMutation<T, WithSurgicalEventId<ARG>>;
  mutationConfig?: UseMutationStateOptions<T, WithSurgicalEventId<ARG>>;
};

export const useMutationForCurrentSurgicalEvent = <
  T,
  ARG extends object | void
>({
  useMutation,
  mutationConfig
}: UseMutationForCurrentSurgicalEventParam<T, ARG>) => {
  const surgicalEventId =
    useCurrentActiveSurgicalEvent()?.currentData?.id ?? '';
  const [mutate, ...mutationProps] = useMutation(mutationConfig);

  const handleMutation = useCallback(
    (arg: ARG) => mutate({ ...arg, surgicalEventId }).unwrap(),
    [mutate, surgicalEventId]
  );

  return [handleMutation, ...mutationProps] as const;
};
